import classNames from "classnames";
import moment from "moment";
import React, { useState } from "react";
import { Icons } from "prefab";
import styles from "../../../../../../styles/Campaigns/CampaignLogs.module.scss";
import { LogRenderContext } from "../LogRenderer";
import GetUpdatedOnBy from "../GetUpdatedOnBy";

const { SortAscendingIcon, SortDescendingIcon, SortIcon } = Icons;

const formatDate = (value) => (value ? moment.utc(value).format("MMM DD, YYYY") : undefined);

const getValidityDate = (entity, field) => {
  const validityField = field === "start" ? "fromDate" : "toDate";
  return entity?.validity?.[validityField] || entity?.[`validity_${field}`];
};

const getChangedEntity = (entities, entity) =>
  entities?.find((changedEntity) => changedEntity.id === entity.id) || {};

const ValidityValue = ({ entity, changedEntity, moveChanges = [] }) => {
  const oldStart = getValidityDate(entity, "start");
  const oldEnd = getValidityDate(entity, "end");
  const entityId = entity?.id;
  const moveStart = moveChanges.find(
    (change) => change.entityId === entityId && change.field === "validity_start"
  );
  const moveEnd = moveChanges.find(
    (change) => change.entityId === entityId && change.field === "validity_end"
  );
  const newStart = moveStart?.newValue || getValidityDate(changedEntity, "start") || oldStart;
  const newEnd = moveEnd?.newValue || getValidityDate(changedEntity, "end") || oldEnd;
  const previousStart = moveStart?.oldValue || oldStart;
  const previousEnd = moveEnd?.oldValue || oldEnd;
  const hasChanged = Boolean(moveStart || moveEnd) || newStart !== oldStart || newEnd !== oldEnd;

  return (
    <span className={styles.moveValidity}>
      <strong>
        {formatDate(newStart)} - {formatDate(newEnd)}
      </strong>
      {hasChanged && (
        <small>
          {formatDate(previousStart)} - {formatDate(previousEnd)}
        </small>
      )}
    </span>
  );
};

const MoveDetails = ({ campaign, changedCampaign, moveChanges }) => (
  <div className={styles.moveDetails}>
    <div className={styles.moveDetailsHeader}>Move Details</div>
    <div className={styles.moveDetailsBody}>
      <div className={styles.moveValidityLabel}>Validity</div>
      <ValidityValue entity={campaign} changedEntity={changedCampaign} moveChanges={moveChanges} />
    </div>
  </div>
);

const SortIndicator = ({ direction, isActive }) => (
  <span
    className={`${styles.moveEntitySortIndicator} ${
      isActive ? "" : styles.moveEntitySortIndicatorInactive
    }`}
  >
    {isActive ? direction === "asc" ? <SortAscendingIcon /> : <SortDescendingIcon /> : <SortIcon />}
  </span>
);

const EntityChanges = ({
  title,
  entityLabel,
  entities,
  changedEntities,
  moveChanges,
  nameField = "name",
}) => {
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  if (!entities?.length) return null;

  const getSortValue = (entity, key) => {
    if (key === "name") return entity[nameField] || entity.id || "";
    if (key === "secondary") {
      return entityLabel === "Media"
        ? entity.targetGroupName || entity.targetGroup?.name || ""
        : entity.noOfScreens ?? entity.screenCount ?? "";
    }
    return getValidityDate(entity, "start") || "";
  };
  const sortedEntities = [...entities].sort((left, right) => {
    const leftValue = getSortValue(left, sortConfig.key);
    const rightValue = getSortValue(right, sortConfig.key);
    const comparison = String(leftValue).localeCompare(String(rightValue), undefined, {
      numeric: true,
    });
    return sortConfig.direction === "asc" ? comparison : -comparison;
  });
  const toggleSort = (key) =>
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  const renderHeader = (label, key) => (
    <button type="button" className={styles.moveEntitySortButton} onClick={() => toggleSort(key)}>
      <span>{label}</span>
      <SortIndicator
        direction={sortConfig.key === key ? sortConfig.direction : "asc"}
        isActive={sortConfig.key === key}
      />
    </button>
  );

  return (
    <div className={styles.moveEntitySection}>
      <div className={styles.moveEntityTitle}>
        {title} <span className={styles.moveEntityCount}>{entities.length}</span>
      </div>
      <div className={styles.moveEntityTable}>
        <div className={styles.moveEntityTableHeader}>
          {renderHeader(entityLabel === "Media" ? "Media Name & ID" : "Target Group Name", "name")}
          {renderHeader(entityLabel === "Media" ? "Target Group" : "Screens", "secondary")}
          {renderHeader("Validity", "validity")}
        </div>
        {sortedEntities.map((entity) => {
          const changedEntity = getChangedEntity(changedEntities, entity);
          return (
            <div key={entity.id} className={styles.moveEntityTableRow}>
              <span>{entity[nameField] || entity.id}</span>
              <span>
                {entityLabel === "Media"
                  ? entity.targetGroupName || entity.targetGroup?.name || "-"
                  : entity.noOfScreens ?? entity.screenCount ?? "-"}
              </span>
              <ValidityValue
                entity={entity}
                changedEntity={changedEntity}
                moveChanges={moveChanges}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CampaignMoved = () => {
  const { log } = React.useContext(LogRenderContext);
  const snapshot = log.dataSnapshot || {};
  const changes = log.changedFields || {};
  const moveChanges = log.changes || [];
  const campaign = snapshot.campaign || {};
  const campaignNew = changes.campaign || {};
  const targetGroups = snapshot.targetGroups || [];
  const medias = snapshot.medias || snapshot.media || [];
  const targetGroupChanges = changes.targetGroups || changes.target_groups || [];
  const mediaChanges = changes.medias || changes.media || [];
  const updatedCampaign = campaignNew.updatedAt ? campaignNew : log;

  return (
    <div className={styles.tabContainer}>
      <div className={styles.badgeContainer}>
        {!!campaign.name && <div className={styles.campaignName}>{campaign.name}</div>}
        <div className={classNames(styles.badge, styles.badgeUpdated)}>
          <div>Moved</div>
        </div>
      </div>
      <GetUpdatedOnBy
        updatedAt={updatedCampaign.updatedAt}
        updatedBy={updatedCampaign.updatedBy}
        timeZone="Asia/Kolkata"
      />
      <MoveDetails campaign={campaign} changedCampaign={campaignNew} moveChanges={moveChanges} />
      <EntityChanges
        title="Target Groups"
        entityLabel="Target Group"
        entities={targetGroups}
        changedEntities={targetGroupChanges}
        moveChanges={moveChanges}
      />
      <EntityChanges
        title="Media"
        entityLabel="Media"
        entities={medias}
        changedEntities={mediaChanges}
        moveChanges={moveChanges}
      />
    </div>
  );
};

export default CampaignMoved;
